import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Divider, Modal } from "antd";
import {
  WifiOutlined,
  CarOutlined,
  HeatMapOutlined,
  CoffeeOutlined,
  LaptopOutlined,
  ShoppingOutlined,
  SmileOutlined,
  CameraOutlined,
  HomeOutlined,
  FireOutlined,
} from "@ant-design/icons";

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: 40px;
  align-items: start;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BookingCard = styled.div`
  position: sticky;
  top: 88px;
  border: 1px solid #e7e7e7;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  @media (max-width: 992px) {
    position: static;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 600;
`;

const AmenityGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const AmenityItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #222;
  font-size: 15px;
  .icon {
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ICONS = [
  { keywords: ["wifi", "internet"], icon: <WifiOutlined className="icon" /> },
  {
    keywords: ["free parking", "parking"],
    icon: <CarOutlined className="icon" />,
  },
  {
    keywords: ["air conditioning", "ac", "central air"],
    icon: <HeatMapOutlined className="icon" />,
  },
  { keywords: ["smoking"], icon: <CoffeeOutlined className="icon" /> },
  {
    keywords: ["workspace", "desk", "work"],
    icon: <LaptopOutlined className="icon" />,
  },
  {
    keywords: ["washer", "washing"],
    icon: <ShoppingOutlined className="icon" />,
  },
  {
    keywords: ["dryer", "hair dryer"],
    icon: <SmileOutlined className="icon" />,
  },
  {
    keywords: ["security camera", "camera"],
    icon: <CameraOutlined className="icon" />,
  },
  {
    keywords: ["balcony", "patio", "terrace"],
    icon: <HomeOutlined className="icon" />,
  },
  {
    keywords: ["central heat", "heating", "heater"],
    icon: <FireOutlined className="icon" />,
  },
];

const getIcon = (name = "") => {
  const lower = name.toLowerCase();
  const match = ICONS.find(({ keywords }) =>
    keywords.some((k) => lower.includes(k))
  );
  return match ? match.icon : <HomeOutlined className="icon" />;
};

export default function BodyGrid({
  amenities = [],
  description,
  deposit,
  dateAvailable,
  priceSummary,
}) {
  const [showAll, setShowAll] = useState(false);
  const preview = useMemo(() => amenities.slice(0, 8), [amenities]);

  return (
    <Grid>
      {/* LEFT COLUMN */}
      <div>
        {amenities.length > 0 && (
          <>
            <SectionTitle>What this place offers</SectionTitle>
            <AmenityGrid>
              {preview.map((a, idx) => (
                <AmenityItem key={`${a}-${idx}`}>
                  {getIcon(a)}
                  <div className="label">{a}</div>
                </AmenityItem>
              ))}
            </AmenityGrid>
            {amenities.length > 8 && (
              <Button size="large" onClick={() => setShowAll(true)}>
                Show all {amenities.length} amenities
              </Button>
            )}
            <Divider style={{ margin: "24px 0" }} />
          </>
        )}

        <SectionTitle>Accessibility features</SectionTitle>
        <p style={{ color: "#717171", marginTop: 4 }}>
          This info was provided by the host and reviewed by Mogul.
        </p>

        {description && (
          <>
            <Divider style={{ margin: "24px 0" }} />
            <SectionTitle>About this place</SectionTitle>
            <p style={{ margin: 0, lineHeight: 1.6 }}>{description}</p>
          </>
        )}
      </div>

      {/* RIGHT COLUMN */}
      <BookingCard>
        <div style={{ fontSize: 20, fontWeight: 700 }}>
          {priceSummary || "$120 for 5 nights"}
        </div>
        {deposit && (
          <div style={{ marginTop: 8 }}>
            <strong>Deposit:</strong> ${deposit}
          </div>
        )}
        {dateAvailable && (
          <div style={{ marginTop: 4 }}>
            <strong>Available:</strong> {dateAvailable}
          </div>
        )}
        <div style={{ color: "#717171", marginTop: 12 }}>
          (Booking widget placeholder — dates, price breakdown, CTA)
        </div>
      </BookingCard>

      {/* MODAL */}
      <Modal
        title="All amenities"
        open={showAll}
        onCancel={() => setShowAll(false)}
        footer={null}
        width={720}
      >
        <AmenityGrid style={{ gridTemplateColumns: "1fr 1fr" }}>
          {amenities.map((a, idx) => (
            <AmenityItem key={`${a}-${idx}`}>
              {getIcon(a)}
              <div className="label">{a}</div>
            </AmenityItem>
          ))}
        </AmenityGrid>
      </Modal>
    </Grid>
  );
}

BodyGrid.propTypes = {
  description: PropTypes.string,
  amenities: PropTypes.arrayOf(PropTypes.string),
  deposit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dateAvailable: PropTypes.string,
  priceSummary: PropTypes.string,
};
